/**
 * Patients API Endpoints
 *
 * Register and list anonymised patients. A patient has NO name: it is
 * identified by a `token` and described by a schema-driven `attributes` map
 * whose shape is the tailored `$schema` document below. The display label is
 * rendered from those attributes via the config `patients.label_format`
 * template — never from a real name. `ward` is NOT an attribute: it is the
 * patient's PLACEMENT (`location_id` + encounter events).
 */

import { api } from '../client';
import type { Page } from '../types';
import { createLogger } from '@/utils/logger';
import { parseResponse } from '../validation';
import { AdmissionRefLinkSchema, DiscloseAdmissionRefResponseSchema } from '../schemas';
import {
  isSchemaDocument,
  schemaDocumentErrors,
  type PatientSchemaDocument,
} from '../patientSchema';

const logger = createLogger('PatientsAPI');

/** One rejected attribute key/value from a 422 write response —
 *  `ApiError.extensions.violations` (RFC 9457 extension member). */
export interface PatientAttributeViolation {
  /** RFC 6901 JSON Pointer into the request body — `/room`, `/shifts/0/date`. */
  field: string;
  code: string;
  /** Prose, and only where the validator had some. It may quote the rejected
   *  value, so a validator that can name the field without quoting it omits
   *  this and the caller renders the field and the code instead. */
  detail?: string;
  allowed_values?: string[];
  /** Set on pattern_mismatch: the RE2 pattern the value must fully match. */
  pattern?: string;
}

/**
 * Metadata-only projection of a patient's admission_ref linkage (design
 * §4 GET /patients/{id}). NEVER carries the plaintext link content — that
 * appears on exactly one surface, the disclosure response below. Absent on
 * a backend that predates the admission_ref pseudonymisation design, or for
 * a unit whose effective schema has no `minted` field at all.
 */
export interface AdmissionRefLink {
  /** The admission_ref field's strategy on the patient's schema at read time
   *  ("passthrough" | "minted" — plain string, forward-compat). */
  strategy: string;
  /** True once an active link row exists for this patient (minted only —
   *  passthrough never links). */
  linked: boolean;
  /** RFC3339 — when the active link was created/last corrected. Absent when unlinked. */
  linked_at?: string;
  /** True when the CALLER holds can_view_phi right
   *  now — gates the reveal affordance. Server-computed; the frontend never
   *  re-derives this. ABSENT on list items, which never pay the per-patient
   *  ACL check — absent is not false. */
  can_disclose?: boolean;
}

export interface PatientResponse {
  token: string;
  /** The record PROJECTED through this caller's read view. Values are TYPED —
   *  a unit may declare an integer or a closed set — and a key another
   *  profession declares is ABSENT, not null: it is not this caller's shape,
   *  and they are not meant to be able to tell it exists. A cleared field is
   *  absent for the same reason. */
  attributes: Record<string, unknown>;
  /** Encounter-lifecycle status vocabulary (config `stay.statuses`) —
   *  plain string, not a TS union: the vocabulary is deployment-config-driven
   *  (mirrors StayResponse.status in stays.ts). */
  status: string;
  /** The patient's current unit, or null when not currently admitted. */
  location_id: string | null;
  /** Metadata about the admission_ref pseudonymisation linkage, never the
   *  link content itself (see AdmissionRefLink). List items carry
   *  strategy/linked/linked_at from one batched read; only the detail GET
   *  adds `can_disclose`. Absent when the patient has no active link row —
   *  which is every patient on a passthrough unit. */
  admission_ref_link?: AdmissionRefLink;
}

export interface RegisterPatientRequest {
  /** The caller's COMPLETE writable view at the target unit, shaped by
   *  `$defs.create` of `GET /patients/$schema?location=<unit>`. Values are
   *  typed, so an integer field takes a number. */
  attributes: Record<string, unknown>;
  /**
   * Target unit. Registration OPENS the patient's first encounter (add =
   * create + place — api-service-go patients.go) and validates `attributes`
   * against THIS unit's effective schema, so the backend needs a real
   * location: this override, else the caller's home unit, else 400
   * PLACEMENT_REQUIRED (the no-orphan guard, #2112). The unit <select> in
   * PatientRegistrationForm carries the location id here — there is no
   * attributes.ward fallback anymore (ward is placement, not an attribute).
   */
  location_id?: string;
  /**
   * Write-only real admission identifiers for a `minted` admission_ref field
   * (design §2/§4) — keyed by the effective schema's declared `link_fields`
   * only; an unknown key is 422. Never echoed back: the response's
   * `attributes.admission_ref` carries the server-minted token instead. Sent
   * ONLY when the target unit's schema has a minted field; a client-supplied
   * `attributes.admission_ref` under that same schema is 422
   * SCHEMA_VIOLATION (`unknown_field`).
   */
  admission_link?: Record<string, string>;
}

/**
 * One page of patients, in the shared list envelope.
 *
 * Whether more follow is the response's `Link: rel="next"`, NOT `items.length`:
 * the `status` filter runs in-memory over the already ACL-paginated page
 * (api-service-go HandleListPatients), so a filtered page can come back SHORTER
 * than the page size while more accessible patients remain — a short page never
 * means "done".
 */
export type ListPatientsResponse = Page<PatientResponse>;

/** `GET /patients` lifecycle filter (AIP-164): server default is `in_progress`
 *  when omitted — `all` is the only way to see discharged/cancelled patients. */
export type PatientStatusFilter = 'in_progress' | 'discharged' | 'cancelled' | 'all';

/**
 * Register a new patient. Returns the created patient (token + attributes).
 */
export async function registerPatient(
  body: RegisterPatientRequest
): Promise<PatientResponse> {
  try {
    logger.debug('Registering patient', {
      attributeKeys: Object.keys(body.attributes),
    });
    const response = await api.post<PatientResponse>('/patients', body);
    logger.info('Patient registered', { token: response.token });
    return response;
  } catch (error) {
    logger.error('Failed to register patient', { error });
    throw error;
  }
}

/**
 * List patients visible to the current user. `status` omitted ⇒ server
 * default `in_progress` (AIP-164) — pass `'all'` to include discharged /
 * cancelled patients (the census board's "Show discharged" toggle).
 *
 * Every consumer treats the result as the complete list, so every page is
 * read, following `Link: rel="next"` to the last.
 */
export async function listPatients(status?: PatientStatusFilter): Promise<PatientResponse[]> {
  try {
    logger.debug('Listing patients', { status });
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    const patients = await api.getAllItems<ListPatientsResponse>(`/patients${query}`);
    logger.info('Patients listed', { count: patients.length, status });
    return patients;
  } catch (error) {
    logger.error('Failed to list patients', { status, error });
    throw error;
  }
}

/**
 * Fetch one patient by token — the source of `admission_ref_link`, which no
 * list response carries. 404 for a missing patient OR one the caller cannot
 * view (existence-non-leaking, api-service-go HandleGetPatient).
 */
export async function getPatient(token: string): Promise<PatientResponse> {
  try {
    logger.debug('Fetching patient', { token });
    return await api.get<PatientResponse>(`/patients/${token}`);
  } catch (error) {
    logger.error('Failed to fetch patient', { token, error });
    throw error;
  }
}

/**
 * Read a patient AND the entity tag of that read.
 *
 * `putPatient` needs the tag: it replaces the caller's complete view, so a
 * blind write would silently overwrite an edit a colleague made between the
 * read the body was computed from and the write.
 */
export async function getPatientWithRevision(
  token: string,
): Promise<{ patient: PatientResponse; etag: string | null }> {
  try {
    logger.debug('Fetching patient with revision', { token });
    const { body, etag } = await api.getWithETag<PatientResponse>(`/patients/${token}`);
    return { patient: body, etag };
  } catch (error) {
    logger.error('Failed to fetch patient', { token, error });
    throw error;
  }
}

/**
 * Replace the caller's COMPLETE WRITABLE VIEW of a patient's record.
 *
 * Not a patch: `attributes` carries every field of the caller's writable view
 * (`$defs.create` of the patient's `$schema`) with the value the record should
 * end up holding. A field OMITTED from that object is CLEARED — there is no
 * `null`, because the vocabulary's floor rejects one like any other wrong
 * value. Fields outside the view (another profession's, or a server-minted
 * one) cannot be named and are left untouched.
 *
 * `etag` is the tag from the read this body was computed from, sent as
 * `If-Match`. 428 without it, 412 when it is stale — re-read and re-apply.
 *
 * 422 carries `PatientAttributeViolation[]` on `ApiError.extensions.violations`
 * (see `parsePatientAttributeViolations`) plus a `schema` URL that resolves to
 * the shape that would have been accepted.
 */
export async function putPatient(
  token: string,
  attributes: Record<string, unknown>,
  etag: string,
): Promise<PatientResponse> {
  try {
    logger.debug('Replacing patient view', { token, keys: Object.keys(attributes) });
    const response = await api.put<PatientResponse>(
      `/patients/${token}`,
      { attributes },
      { headers: { 'If-Match': etag } },
    );
    logger.info('Patient view replaced', { token });
    return response;
  } catch (error) {
    logger.error('Failed to replace patient view', { token, error });
    throw error;
  }
}

/**
 * The tailored schema document for /patients at a unit: what THIS caller writes
 * and reads there, as JSON Schema 2020-12.
 *
 * Not zod-validated. A JSON Schema is validated by a JSON Schema validator, and
 * a zod mirror of one would be a second copy of the shape this whole engine
 * exists to keep in one place.
 */
export async function getPatientsSchema(locationId?: string): Promise<PatientSchemaDocument> {
  const query = locationId ? `?location=${encodeURIComponent(locationId)}` : '';
  try {
    logger.debug('Fetching the patients schema', { locationId });
    const doc = await api.get<unknown>(`/patients/$schema${query}`);
    return asSchemaDocument(doc, `GET /patients/$schema${query}`);
  } catch (error) {
    logger.error('Failed to fetch the patients schema', { locationId, error });
    throw error;
  }
}

/**
 * Check a `$schema` response against the JSON Schema meta-schema before any
 * surface renders or validates against it.
 *
 * The meta-schema is the dialect's own definition of what a schema IS, so this
 * is the one check that does not restate the document's shape. A document that
 * fails it cannot drive a form or a validator, and failing here names the
 * endpoint instead of surfacing later as an unexplained blank form.
 */
function asSchemaDocument(doc: unknown, source: string): PatientSchemaDocument {
  if (!isSchemaDocument(doc)) {
    throw new Error(`${source} returned an invalid JSON Schema: ${schemaDocumentErrors()}`);
  }
  return doc;
}

/**
 * The tailored schema document for ONE patient, resolved from that patient's
 * own placement — so a transfer changes it with nothing on the record changing.
 */
export async function getPatientSchema(token: string): Promise<PatientSchemaDocument> {
  try {
    logger.debug('Fetching a patient schema', { token });
    const doc = await api.get<unknown>(`/patients/${token}/$schema`);
    return asSchemaDocument(doc, `GET /patients/${token}/$schema`);
  } catch (error) {
    logger.error('Failed to fetch a patient schema', { token, error });
    throw error;
  }
}

/**
 * Narrow an `ApiError.extensions.violations` payload (untyped — a generic
 * problem+json extension bag) into `PatientAttributeViolation[]`, dropping any
 * entry that doesn't carry `field` and `code` strings rather than crashing the
 * attribute editor on an unexpected server shape.
 *
 * `detail` is NOT required. It is optional on the wire, and requiring it here
 * silently dropped every violation a validator raised without prose — the form
 * then showed nothing at all for a request the server had refused.
 */
export function parsePatientAttributeViolations(
  extensions: Record<string, unknown> | undefined,
): PatientAttributeViolation[] {
  const raw = extensions?.violations;
  if (!Array.isArray(raw)) return [];
  return raw.filter((v): v is PatientAttributeViolation => {
    if (typeof v !== 'object' || v === null) return false;
    const o = v as Record<string, unknown>;
    return typeof o.field === 'string' && typeof o.code === 'string';
  });
}

/**
 * Correct a patient's admission_ref linkage (design §4 POST
 * /patients/{id}/admission-ref/link) — supersedes the active link row with a
 * fresh one; the token itself never changes. Used both to fix a mistyped
 * case number on an already-linked patient AND to link a legacy patient
 * whose unit has since flipped to `minted` (`linked: false` today). 404
 * under a `passthrough` field (no linkage exists to correct); 422 on an
 * unknown/deny-listed link-field key. Response is the updated linkage
 * metadata only — never the link content (that stays behind the one
 * disclosure door below).
 */
export async function relinkAdmissionRef(
  token: string,
  link: Record<string, string>,
): Promise<AdmissionRefLink> {
  try {
    logger.debug('Relinking admission ref', { token, keys: Object.keys(link) });
    const response = await api.post(`/patients/${token}/admission-ref/link`, { link });
    const parsed = parseResponse(
      AdmissionRefLinkSchema,
      response,
      `POST /patients/${token}/admission-ref/link`,
    );
    logger.info('Admission ref relinked', { token });
    return parsed;
  } catch (error) {
    logger.error('Failed to relink admission ref', { token, error });
    throw error;
  }
}

/** POST /patients/{id}/admission-ref/disclosures request body — the reveal
 *  door's audit necessity record. `justification` is optional free text the
 *  caller may add to the audit record. */
export interface DiscloseAdmissionRefRequest {
  reason_code: string;
  justification?: string;
}

/** The ONE surface that ever carries the plaintext admission link — render
 *  it once and discard; never persist into a query cache or component state
 *  that survives the dialog closing. */
export interface DiscloseAdmissionRefResponse {
  link: Record<string, string>;
  /** RFC3339. */
  disclosed_at: string;
}

/**
 * Reveal a patient's admission_ref link content (design §4 — "THE one
 * door"). Gate: `can_view_phi`, server-enforced. 404 when unlinked, under
 * `passthrough`, or the caller cannot view this patient at all
 * (existence-non-leaking). 422 INVALID_REASON_CODE is recoverable — the
 * caller re-submits with a corrected reason code, not a terminal error.
 */
export async function discloseAdmissionRef(
  token: string,
  body: DiscloseAdmissionRefRequest,
): Promise<DiscloseAdmissionRefResponse> {
  try {
    logger.debug('Requesting admission ref disclosure', { token, reasonCode: body.reason_code });
    const response = await api.post(`/patients/${token}/admission-ref/disclosures`, body);
    const parsed = parseResponse(
      DiscloseAdmissionRefResponseSchema,
      response,
      `POST /patients/${token}/admission-ref/disclosures`,
    );
    logger.info('Admission ref disclosed', { token });
    return parsed;
  } catch (error) {
    logger.error('Failed to disclose admission ref', { token, error });
    throw error;
  }
}
